/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { IconChevronLeft, IconCloudDown, IconEdit } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useDriverUpdateMutation,
  useFetchDriverDetailsData,
} from "../../apis/driverApis";
import formatDate from "../../utilities/helper";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import AddDriverModal from "./_components/AddDriverModal";
import NewCommonModal from "../../components/common/NewCommonModal";
import { usePermission } from "../../hooks/usePermissionTabs";

interface DocumentLinkProps {
  value?: string;
  docUrl?: string;
  label: string;
}

const DocumentLink = ({ value, docUrl, label }: DocumentLinkProps) => {
  if (!value && !docUrl) return null;

  return (
    <div className="flex items-center gap-2">
      {value && <span className="font-medium text-gray-800">{value}</span>}
      {docUrl && (
        <a
          href={docUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-cente hover:bg-[#FBD037]/20 p-0.5 px-1 rounded"
          title={`View ${label} Document`}
          aria-label={`View ${label} Document`}
        >
          <IconCloudDown strokeWidth={1.5} size={22} />
        </a>
      )}
    </div>
  );
};

const TwoCol = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="text-sm text-[#212121] flex justify-between items-center gap-1 border-b last:border-0 py-3 px-4">
    <p className="capitalize ">{label}:</p>
    <p className="font-medium "> {value}</p>
  </div>
);

const Section = ({
  title,
  children,
}: {
  title?: string;
  children: React.ReactNode;
}) => (
  <div className="p-4 bg-[#F7F6F2] rounded-3xl border border-[#EFEFEF] space-y-2">
    <div className="grid grid-cols-12 gap-x-6 ">{children}</div>
  </div>
);

export default function ViewDriverDetails() {
  const { id } = useParams();
  const [editDialog, setEditDialog] = useState<boolean>(false);
  const { mutate, isPending: isRequestPending } = useDriverUpdateMutation(
    id ?? "",
  );
  const [selectedDocument, setSelectedDocument] = useState<any>({
    iqamaDoc: false,
    licenseDoc: false,
    image: false,
  });

  const { data: driverDetails, refetch: fetchDriverDetails } =
    useFetchDriverDetailsData(id ?? "");
  // console.log(driverDetails, "Driver Details");

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm<any>();

  useEffect(() => {
    if (id) {
      fetchDriverDetails();
      // fetchStaffDocuments();
    }
  }, [id]);

  const handleEditDriverToggle = () => {
    setEditDialog(!editDialog);
    if (!editDialog) {
      reset();
    }
  };

  const { allowed: canUpdateDriver, isLoading } = usePermission(
    "drivers",
    "update",
  );

  const handleDriverUpdate = (data: any) => {
    let { image, iqamaDoc, licenseDoc, ...otherData } = data;
    mutate(
      {
        ...otherData,
        ...(selectedDocument?.iqamaDoc && { iqamaDoc: data?.iqamaDoc }),
        ...(selectedDocument?.licenseDoc && { licenseDoc: data?.licenseDoc }),
        ...(selectedDocument?.photo && { image: data?.image }),
      },
      {
        onSuccess: () => {
          setSelectedDocument({
            iqamaDoc: false,
            licenseDoc: false,
            image: false,
          });
          fetchDriverDetails();
          handleEditDriverToggle();
          setEditDialog(false);
          toast.success("Driver Update Successfully.");
          navigate("/users?tab=Drivers");
        },
        onError: (error: any) => {
          const errorMessage =
            error?.response?.data?.error?.message ||
            error?.response?.data?.message ||
            error?.message ||
            (typeof error === "string" ? error : null) ||
            "Failed to Update Driver";

          toast.error(errorMessage);
        },
      },
    );
  };

  return (
    <>
      <div className="p-4 pt-0 space-y-4">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(-1)}
              className="p-1.5 border border-[#D2D2D2] rounded-full hover:bg-[#F7F6F2] transition"
            >
              <IconChevronLeft strokeWidth={1.5} size={22} />
            </button>
            <div className="flex items-center gap-2">
              <img
                src={driverDetails?.image || "/img/dummy-blank-profile.png"}
                className="w-10 h-10 rounded-full border object-cover"
                alt=""
              />
              <h1 className="text-2xl text-[#212121] tracking-[-0.5px]  ">
                {driverDetails?.driverName}
              </h1>
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          <Section title={""}>
            <div className="flex justify-between items-center col-span-12 pb-3 mb-4 lg:mb-0">
              <h3 className="text-[#212121] font-semibold text-lg">
                Driver Details{" "}
              </h3>
              {canUpdateDriver && (
                <button
                  onClick={() => {
                    setEditDialog(true);
                  }}
                  className="flex items-center gap-1  p-2.5  text-sm rounded-full border border-[#FFFFFF] bg-[#F7F6F2]"
                >
                  <IconEdit stroke={1.5} size={24} />
                </button>
              )}
            </div>
            <div className="bg-[#FCFDF8] border rounded-2xl overflow-hidden col-span-12 lg:col-span-4 mb-4 lg:mb-0">
              <h3 className="text-[#212121] font-medium text-base capitalize border-b border-gray-300 border-dashed py-2 w-full bg-[#F7F6F2] rounded-t-xl px-4">
                Driver Info{" "}
              </h3>

              <TwoCol label="Driver Name" value={driverDetails?.driverName} />
              <TwoCol label="Driver Code" value={driverDetails?.driver_code} />
              <TwoCol label="Route Code" value={driverDetails?.route_codes} />
              <TwoCol label="Driver Type" value={driverDetails?.driver_type} />
              <TwoCol label="Mobile Number" value={driverDetails?.mobile_no} />
              <TwoCol label="Mail" value={driverDetails?.email} />
              <TwoCol label="Warehouse" value={driverDetails?.warehouseName} />
              <TwoCol label="Country" value={driverDetails?.countryName} />
              <TwoCol label="City" value={driverDetails?.cityName} />
              <TwoCol label="District" value={driverDetails?.districtName} />
              <TwoCol
                label="License Number"
                value={driverDetails?.license_number}
              />
              <TwoCol
                label="Iqama Number"
                value={driverDetails?.iqama_number}
              />
              <TwoCol label="Access" value="LM, FM, Both" />
              <TwoCol label="Address" value={driverDetails?.address} />
              <TwoCol label="Time Slot" value={driverDetails?.timeslotname} />
              {driverDetails?.darkStoreName && (
                <TwoCol
                  label="Dark Store"
                  value={driverDetails?.darkStoreName}
                />
              )}
            </div>
            <div className="bg-[#FCFDF8] border rounded-2xl overflow-hidden col-span-12 lg:col-span-4 mb-4 lg:mb-0">
              <h3 className="text-[#212121] font-medium text-base capitalize border-b border-gray-300 border-dashed py-2 w-full bg-[#F7F6F2] rounded-t-xl px-4">
                Vehicle info
              </h3>
              <div className="grid grid-cols-1 ">
                <TwoCol
                  label="Vehicle Type"
                  value={driverDetails?.vehicle_type}
                />
                <TwoCol
                  label="Vehicle Name"
                  value={driverDetails?.vehicleName}
                />
                <TwoCol
                  label="Vehicle number"
                  value={driverDetails?.vehicle_number}
                />
                <TwoCol
                  label="Assigned Date"
                  value={formatDate(driverDetails?.assigned_on) || ""}
                />
              </div>
            </div>
            <div className="bg-[#FCFDF8] border rounded-2xl overflow-hidden col-span-12 lg:col-span-4 mb-4 lg:mb-0">
              <h3 className="text-[#212121] font-medium text-base capitalize border-b border-gray-300 border-dashed py-2 w-full bg-[#F7F6F2] rounded-t-xl px-4">
                Documents
              </h3>
              <div className="grid grid-cols-1 ">
                <TwoCol
                  label="Iqama"
                  value={
                    <DocumentLink
                      value={driverDetails?.iqama_number}
                      docUrl={driverDetails?.iqama_doc}
                      label="Iqama"
                    />
                  }
                />
                <TwoCol
                  label="License Number"
                  value={
                    <DocumentLink
                      value={driverDetails?.license_number}
                      docUrl={driverDetails?.license_doc}
                      label="License"
                    />
                  }
                />
                <TwoCol
                  label="Vehicle Doc (RC)"
                  value={
                    <DocumentLink
                      value={driverDetails?.rc_number}
                      docUrl={driverDetails?.rc_docs}
                      label="Vehicle RC"
                    />
                  }
                />
                <TwoCol
                  label="Vehicle Inspection"
                  value={
                    <DocumentLink
                      docUrl={driverDetails?.inspection_paper}
                      // value={driverDetails?.vehicleInspection || "NA"}
                      label="Vehicle Inspection"
                    />
                  }
                />
              </div>
            </div>
          </Section>
        </div>
      </div>
      <NewCommonModal
        title={"Edit Driver"}
        open={editDialog}
        closeButtonText={"Cancel"}
        onClose={handleEditDriverToggle}
        actionButtonText="Update"
        actionButtonOnClick={handleSubmit(handleDriverUpdate)}
        actionButtonProps={{ disabled: isRequestPending }}
      >
        <AddDriverModal
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          clearErrors={clearErrors}
          control={control}
          setSelectedDocument={setSelectedDocument}
          driverData={driverDetails}
        />
      </NewCommonModal>
    </>
  );
}
